import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Edit,
  Trash2,
  Search,
  AlertTriangle,
  Plus,
  Minus,
  Save,
  Undo2,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toaster';
import { useAuth, useHasRole } from '@/features/auth/AuthProvider';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import { type InventoryItem } from '@/types/schemas';
import {
  fetchAllInventory,
  fetchStoreInventoryMap,
  mergeWithStoreStock,
  deleteInventoryItem,
} from '../inventoryQueries';
import { fetchCategories, fetchBrands } from '../setupQueries';
import { bulkStockUpdate } from '../bulkStockUpdate';
import { InventoryFormDialog } from '../InventoryFormDialog';
import { logActivity } from '@/lib/activityLog';
import { formatCurrency, cn } from '@/lib/utils';

type SortField = 'name' | 'category' | 'brand' | 'quantity' | 'sellingPrice';

export function ProductsTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = useHasRole('owner');
  const canDelete = useHasRole('owner');
  const currentStoreId = useCurrentStore((s) => s.currentStoreId);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventoryItem | null>(null);

  /* ─── Temporary stock modifications ─── */
  const [stockOverrides, setStockOverrides] = useState<Record<string, number>>({});

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['inventory'],
    queryFn: fetchAllInventory,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: fetchCategories,
  });
  const { data: brands = [] } = useQuery({
    queryKey: ['inventory-brands', categoryFilter],
    queryFn: () => (categoryFilter ? fetchBrands(categoryFilter) : fetchBrands()),
  });
  const { data: storeMap = {} } = useQuery({
    queryKey: ['store-inventory-map', currentStoreId],
    queryFn: () => (currentStoreId ? fetchStoreInventoryMap(currentStoreId) : Promise.resolve({})),
    enabled: !!currentStoreId,
  });

  const withStock = useMemo(() => mergeWithStoreStock(items, storeMap), [items, storeMap]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = withStock;
    if (categoryFilter) {
      list = list.filter((i) => i.categoryId === categoryFilter || i.category === categoryFilter);
    }
    if (brandFilter) {
      const selectedBrand = brands.find((b) => b.id === brandFilter);
      list = list.filter(
        (i) =>
          i.brandId === brandFilter ||
          (selectedBrand && i.brand.toLowerCase() === selectedBrand.name.toLowerCase()),
      );
    }
    if (q) {
      list = list.filter((i) =>
        `${i.itemName} ${i.sku} ${i.brand} ${i.category}`.toLowerCase().includes(q),
      );
    }
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = (a.itemName || a.brand).localeCompare(b.itemName || b.brand);
      else if (sortField === 'category') cmp = a.category.localeCompare(b.category);
      else if (sortField === 'brand') cmp = a.brand.localeCompare(b.brand);
      else if (sortField === 'quantity') cmp = a.quantity - b.quantity;
      else cmp = a.sellingPrice - b.sellingPrice;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [withStock, search, categoryFilter, brandFilter, brands, sortField, sortDir]);

  /* ─── Plus / Minus handlers ─── */
  function handleIncrement(item: InventoryItem) {
    const currentVal = stockOverrides[item.id] !== undefined ? stockOverrides[item.id] : item.quantity;
    setStockOverrides((prev) => ({
      ...prev,
      [item.id]: currentVal + 1,
    }));
  }

  function handleDecrement(item: InventoryItem) {
    const currentVal = stockOverrides[item.id] !== undefined ? stockOverrides[item.id] : item.quantity;
    if (currentVal <= 0) return;
    setStockOverrides((prev) => ({
      ...prev,
      [item.id]: Math.max(0, currentVal - 1),
    }));
  }

  function handleCancelChanges() {
    setStockOverrides({});
  }

  /* ─── Save Changes (Bulk stock update) ─── */
  const hasChanges = Object.keys(stockOverrides).some((id) => {
    const item = withStock.find((i) => i.id === id);
    return item && stockOverrides[id] !== item.quantity;
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const itemsToUpdate = Object.entries(stockOverrides)
        .filter(([id, qty]) => {
          const item = withStock.find((i) => i.id === id);
          return item && item.quantity !== qty;
        })
        .map(([productId, stock]) => ({ productId, stock }));

      if (itemsToUpdate.length === 0) return { updatedCount: 0 };

      return bulkStockUpdate(itemsToUpdate, {
        storeId: currentStoreId || undefined,
        actorId: user?.uid ?? null,
        actorName: user?.displayName ?? user?.email ?? null,
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['store-inventory-map'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setStockOverrides({});
      toast({
        title: 'Stock changes saved',
        description: `Successfully updated stock for ${res.updatedCount} product(s).`,
        variant: 'success',
      });
    },
    onError: (err) => {
      toast({
        title: 'Failed to save stock',
        description: String(err instanceof Error ? err.message : err),
        variant: 'destructive',
      });
    },
  });

  /* ─── Delete mutation ─── */
  const remove = useMutation({
    mutationFn: async (item: InventoryItem) => {
      await deleteInventoryItem(item.id);
      return item;
    },
    onSuccess: (deleted) => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast({
        title: 'Product deleted',
        description: `${deleted.itemName} has been deleted.`,
        variant: 'success',
      });
      logActivity({
        action: 'product_deleted',
        title: 'Product Deleted',
        description: `Deleted "${deleted.itemName}" from ${deleted.category} / ${deleted.brand}`,
        actorId: user?.uid ?? null,
        actorName: user?.displayName ?? user?.email ?? null,
        entityType: 'product',
        entityId: deleted.id,
      });
      setDeleteTarget(null);
      setStockOverrides((prev) => {
        const next = { ...prev };
        delete next[deleted.id];
        return next;
      });
    },
    onError: (e) =>
      toast({ title: 'Delete failed', description: String(e), variant: 'destructive' }),
  });

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search by name, SKU, brand…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="h-9 rounded-md border px-2 text-sm bg-background"
          value={categoryFilter ?? ''}
          onChange={(e) => {
            setCategoryFilter(e.target.value || null);
            setBrandFilter(null);
          }}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          className="h-9 rounded-md border px-2 text-sm bg-background"
          value={brandFilter ?? ''}
          onChange={(e) => setBrandFilter(e.target.value || null)}
        >
          <option value="">All brands</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        {canWrite && (
          <Button
            variant="gold"
            disabled={!currentStoreId}
            title={!currentStoreId ? 'Choose a store first' : 'Create product'}
            onClick={() => { setEditing(null); setOpen(true); }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Create product
          </Button>
        )}
      </div>

      <div className="mb-2 flex flex-wrap gap-2 text-xs">
        {(['category', 'brand', 'name', 'quantity', 'sellingPrice'] as SortField[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => toggleSort(f)}
            className={cn(
              'rounded border px-2 py-1',
              sortField === f ? 'bg-gold-100 border-gold-400 text-gold-900 font-medium' : 'text-muted-foreground',
            )}
          >
            {f} {sortField === f ? (sortDir === 'asc' ? '↑' : '↓') : ''}
          </button>
        ))}
      </div>

      {isLoading && <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && filtered.length === 0 && (
        <p className="p-8 text-center text-sm text-muted-foreground">No products found.</p>
      )}

      <div className="overflow-x-auto rounded-lg border bg-card shadow-2xs">
        <div className="grid min-w-[760px] grid-cols-[1fr_1fr_1.5fr_0.8fr_0.9fr_6rem] gap-2 border-b bg-muted/40 px-3 py-2.5 text-[11px] font-semibold uppercase text-muted-foreground">
          <span>Category</span>
          <span>Brand</span>
          <span>Product Name</span>
          <span>Stock</span>
          <span>Price</span>
          <span className="text-center">Actions</span>
        </div>

        <div className="divide-y">
          {filtered.map((it) => {
            const isModified =
              stockOverrides[it.id] !== undefined && stockOverrides[it.id] !== it.quantity;
            const displayQty =
              stockOverrides[it.id] !== undefined ? stockOverrides[it.id] : it.quantity;

            return (
              <div
                key={it.id}
                className={cn(
                  'grid min-w-[760px] grid-cols-[1fr_1fr_1.5fr_0.8fr_0.9fr_6rem] items-center gap-2 px-3 py-2 text-sm transition-colors',
                  isModified && 'bg-amber-500/10 border-l-4 border-l-amber-500',
                )}
              >
                <span className="text-xs text-muted-foreground truncate">{it.category}</span>
                <span className="text-xs text-muted-foreground truncate">{it.brand}</span>
                <div className="min-w-0">
                  <div className="font-medium truncate">{it.itemName || `${it.brand} ${it.subBrand}`.trim()}</div>
                  {it.sku && <div className="truncate font-mono text-[10px] text-muted-foreground">{it.sku}</div>}
                  {it.lowStock && (
                    <Badge variant="warning" className="mt-1 gap-1 text-[10px]">
                      <AlertTriangle className="h-3 w-3" /> low
                    </Badge>
                  )}
                </div>

                {/* Stock column with modified indicator */}
                <div className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      'tabular-nums font-medium',
                      displayQty === 0 && 'text-destructive font-bold',
                      isModified && 'text-amber-700 font-bold dark:text-amber-400',
                    )}
                  >
                    {displayQty}
                  </span>
                  {isModified && (
                    <span className="text-[10px] text-muted-foreground line-through tabular-nums">
                      ({it.quantity})
                    </span>
                  )}
                </div>

                {/* Price column */}
                <span className="tabular-nums font-medium">{formatCurrency(it.sellingPrice)}</span>

                {/* Actions column: Minus, Plus, Edit, Delete */}
                <div className="flex items-center justify-center gap-0.5">
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => handleDecrement(it)}
                      disabled={displayQty <= 0}
                      title="Decrease stock by 1"
                      aria-label="Decrease stock by 1"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => handleIncrement(it)}
                      title="Increase stock by 1"
                      aria-label="Increase stock by 1"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => { setEditing(it); setOpen(true); }}
                      title="Edit product"
                      aria-label="Edit product"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteTarget(it)}
                      title="Delete product"
                      aria-label="Delete product"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating Save / Cancel Bar */}
      {hasChanges && (
        <div className="sticky bottom-4 z-30 mt-4 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-background/95 p-3 shadow-lg backdrop-blur-sm">
          <div className="text-sm">
            <span className="font-semibold text-amber-700 dark:text-amber-400">
              {
                Object.keys(stockOverrides).filter((id) => {
                  const item = withStock.find((i) => i.id === id);
                  return item && stockOverrides[id] !== item.quantity;
                }).length
              }
            </span>{' '}
            product stock value(s) modified
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelChanges}
              disabled={saveMutation.isPending}
            >
              <Undo2 className="h-4 w-4 mr-1" />
              Cancel Changes
            </Button>
            <Button
              variant="gold"
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              {saveMutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(v) => {
          if (!v) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Product</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <strong>{deleteTarget?.itemName || deleteTarget?.brand}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget);
              }}
              disabled={remove.isPending}
            >
              {remove.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1" />
              )}
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Edit / Create Dialog */}
      <InventoryFormDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        defaultCategoryId={categoryFilter ?? undefined}
        defaultBrandId={brandFilter ?? undefined}
      />
    </>
  );
}
